import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle } from 'lucide-react';
import { ElementRef, useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ConfirmModal } from './ConfirmModal';
import { FormCheckbox } from './FormCheckbox';
import { IntegrationSwitch } from './IntegrationSwitch';

import { Alert, AlertDescription } from '@/components/ui/Alert';
import { useGetSeo } from '@/hooks/useGetSeo';
import { useUpdateSeo } from '@/hooks/useUpdateSeo';

const integrationFormSchema = z.object({
  productName: z.boolean(),
  productDescription: z.boolean(),
  altDescriptionProduct: z.boolean(),
  productTags: z.boolean(),
  altDescriptionImage: z.boolean(),
});

export type IntegrationFormData = z.infer<typeof integrationFormSchema>;

export function IntegrationForm() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.SeoContent.components.IntegrationForm',
  });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const submitButtonRef = useRef<ElementRef<'button'>>(null);

  const { seo } = useGetSeo();

  const { updateSeo } = useUpdateSeo();

  const { control, handleSubmit, watch } = useForm<IntegrationFormData>({
    resolver: zodResolver(integrationFormSchema),
    defaultValues: {
      productName: !!seo?.productName,
      productDescription: !!seo?.productDescription,
      altDescriptionProduct: !!seo?.altDescriptionProduct,
      productTags: !!seo?.productTags,
      altDescriptionImage: !!seo?.altDescriptionImage,
    },
  });

  const productName = watch('productName');

  const handleToggleConfirmModal = useCallback(() => {
    setIsConfirmModalOpen((state) => !state);
  }, []);

  const handleClickSubmitButton = useCallback(() => {
    submitButtonRef.current?.click();
    setIsConfirmModalOpen(!isConfirmModalOpen);
  }, [isConfirmModalOpen]);

  const handleIntegration = handleSubmit(async (data) => {
    await updateSeo({
      ...data,
      sync: !seo?.sync,
    });
  });

  if (!seo) {
    return null;
  }

  return (
    <>
      <form className="space-y-4" onSubmit={handleIntegration} noValidate>
        <IntegrationSwitch
          isActive={!!seo?.sync}
          onToggleConfirmModal={handleToggleConfirmModal}
        />

        <div className="mt-4 grid grid-cols-2 gap-4">
          <FormCheckbox
            name="productName"
            label={t('labels.productName')}
            disabled={seo.sync}
            control={control}
          />

          <FormCheckbox
            name="productDescription"
            label={t('labels.productDescription')}
            disabled={seo.sync}
            control={control}
          />

          <FormCheckbox
            name="altDescriptionProduct"
            label={t('labels.altDescriptionProduct')}
            disabled={seo.sync}
            control={control}
          />

          <FormCheckbox
            name="productTags"
            label={t('labels.productTags')}
            disabled={seo.sync}
            control={control}
          />

          <FormCheckbox
            name="altDescriptionImage"
            label={t('labels.altDescriptionImage')}
            disabled={seo.sync}
            control={control}
          />
        </div>

        <button ref={submitButtonRef} type="submit" className="hidden" />

        {productName && (
          <Alert className="flex w-full max-w-[800px] items-center gap-2 border-yellow-500 bg-yellow-50 text-yellow-800">
            <div>
              <AlertCircle className="h-4 w-4 text-yellow-500" />
            </div>

            <AlertDescription>
              <strong>{t('warning.title')}</strong>
              {t('warning.description')}
            </AlertDescription>
          </Alert>
        )}
      </form>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        isSyncEnabled={seo.sync}
        onToggle={handleToggleConfirmModal}
        onConfirm={handleClickSubmitButton}
      />
    </>
  );
}
